import React from 'react'
import { Box, Container, Grid, Pagination, PaginationItem, Paper, Typography } from '@mui/material'
import moment from 'moment'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import Breadcrumbs from '@components/Breadcrumbs'
import { useBlogs } from '@api/blogs'
import useQueryState from '@components/useQueryState'
import NextLink from '@imports/NextLink'
import { useRouter } from 'next/router'
import SearchTextField from '@imports/SearchTextField'

export function BlogsSection() {
  const { t, i18n } = useTranslation(['common', 'blog'])
  const [page, setPage] = useQueryState('page', { defaultValue: 1, type: 'integer' })
  const [search, setSearch] = useQueryState('q', { defaultValue: '', type: 'string' })
  const router = useRouter()
  const blogsQuery = useBlogs(page, router.locale, undefined, search)
  React.useEffect(() => {
    if (page !== 1) setPage(1)
  }, [router.locale])
  return (
    <Box
      mt={10}
      mb={7}
    >
      <Container maxWidth="xl">
        <Box mb={5}>
          <Grid
            container
            spacing={1}
            flexDirection="row-reverse"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Grid
              item
              xs={12}
              sm="auto"
            >
              <SearchTextField
                fullWidth
                placeholder={t('common:search')}
                searchState={[search, setSearch]}
                backgroundColor="paper.main"
                size="small"
                isLoading={blogsQuery.isRefetching}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '40px',
                    backgroundColor: 'paper.main',
                    pl: 2,
                    pr: 3,
                    transition: (theme) =>
                      theme.transitions.create(['width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                      }),
                  },
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm
            >
              <Typography
                variant="h5"
                gutterBottom
              >
                {t('blog:Blog')}
              </Typography>
            </Grid>
          </Grid>
          <Breadcrumbs items={[{ text: t('blog:home'), url: '/' }, { text: t('blog:blog') }]} />
        </Box>
        <Grid
          container
          spacing={4}
          alignItems="stretch"
        >
          {blogsQuery.data?.data?.map(
            ({ id, slug, title, description, image, created_at: createdAt }) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                xl={3}
                key={id}
              >
                <Paper
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    height: '100%',
                    cursor: 'pointer',
                    display: 'block',
                    transition: (theme) =>
                      theme.transitions.create(['transform'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                      }),
                    ':hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                  component={NextLink}
                  href={`/blog/${slug}`}
                >
                  <Image
                    loading="eager"
                    src={image ?? '/images/video-poster.webp'}
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'cover',
                    }}
                    alt="blog image"
                    width="1000"
                    height="1000"
                  />
                  <Box
                    px={2}
                    pt={0.5}
                    pb={1.5}
                  >
                    <Typography
                      variant="caption"
                      color="neutral.dark"
                      fontWeight={500}
                      display="block"
                      sx={{ pb: 2 }}
                    >
                      {moment(createdAt).locale(i18n.language).format('LL')}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      gutterBottom
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontSize={16}
                      gutterBottom
                      sx={{
                        fontSize: '16px !important',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: '3  ',
                        lineClamp: '3 ',
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )
          )}
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
          >
            <Pagination
              color="primary"
              sx={{
                mt: 5,
                '& .MuiSvgIcon-root': {
                  transform: 'scale(1) !important',
                },
              }}
              count={blogsQuery.data?.meta?.last_page ?? 0}
              renderItem={(item) => (
                <PaginationItem
                  sx={{ fontFamily: 'var(--cairo-font) !important' }}
                  {...item}
                />
              )}
              page={page}
              onChange={(_, value) => setPage(value)}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default BlogsSection
